import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CheckCircle2, Clock, Eye, Download, X, FileText, Lock, Mail, Upload } from 'lucide-react';
import { api, openFileInNewTab } from '../../lib/api';

/**
 * Native CRO form — replaces the "Stage6 - CRO" Google Form.
 *
 * Same fields the form captured (CRO document, Validity, Depot details,
 * Size/Type, Location; who and when are taken from the login), plus:
 *   • the indent's containers as a checklist — released ones locked, pending
 *     ones selectable, so a CRO can cover just the boxes that have arrived;
 *   • the PARTIAL / COMPLETE flag on top, recomputed by the server after every
 *     submission;
 *   • an optional tax invoice — PDF only, checked by its bytes here and again
 *     on the server, and never a reason to block the CRO.
 *
 * Backend: routes/procurement/cro.routes.ts → services/procurement/cro.ts.
 */

type Board = {
  indentId: string; indentType: string; productCode: string; quantity: number;
  applies: boolean; legacy: boolean; legacyDocUrl: string;
  containers: Array<{
    containerNo: string; croDone: boolean; croRef: string; croAt: string | null;
    croSource: 'native' | 'legacy' | ''; gateStatus: string; gatedIn: boolean; gateInEligible: boolean;
  }>;
  unregistered: number; total: number; croCompleted: number; pending: number;
  status: 'COMPLETE' | 'PARTIAL' | 'NONE'; label: string;
  submissions: Array<{
    croRef: string; croNumber: string; containerNos: string[]; validity: string; depot: string;
    sizeType: string; location: string; remarks: string; submittedAt: string; submittedByName: string;
    croDoc: { fileId: string; name: string } | null; taxInvoice: { fileId: string; name: string } | null;
    email: { status: string; at: string | null; error: string };
  }>;
  recipients: { to: string[]; cc: string[] };
};

type Picked = { file: File; fileId: string; name: string; url: string } | null;

const fmt = (iso?: string | null) => (iso
  ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
  : '');

/** First bytes of a file — what it is, regardless of its name. */
async function sniff(file: File): Promise<'pdf' | 'png' | 'jpeg' | ''> {
  const b = new Uint8Array(await file.slice(0, 8).arrayBuffer());
  if (b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46 && b[4] === 0x2d) return 'pdf';
  if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return 'png';
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'jpeg';
  return '';
}

export default function CroFormModal({ indentId, onClose }: { indentId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery<Board>({
    queryKey: ['procurement', 'cro', indentId],
    queryFn: () => api.get(`/api/procurement/cro/${encodeURIComponent(indentId)}`),
  });
  const b = q.data;

  const [sel, setSel] = useState<Set<string>>(new Set());
  const [croNumber, setCroNumber] = useState('');
  const [validity, setValidity] = useState('');
  const [depot, setDepot] = useState('');
  const [sizeType, setSizeType] = useState('');
  const [location, setLocation] = useState('');
  const [remarks, setRemarks] = useState('');
  const [croDoc, setCroDoc] = useState<Picked>(null);
  const [tax, setTax] = useState<Picked>(null);
  const [uploading, setUploading] = useState<'' | 'cro-doc' | 'tax-invoice'>('');
  const [busy, setBusy] = useState(false);
  const [lastMail, setLastMail] = useState<{ status: string; error?: string } | null>(null);

  // Object URLs for local previews — released when replaced or on close.
  useEffect(() => () => { if (croDoc?.url) URL.revokeObjectURL(croDoc.url); }, [croDoc]);
  useEffect(() => () => { if (tax?.url) URL.revokeObjectURL(tax.url); }, [tax]);

  const pending = useMemo(() => (b?.containers || []).filter((c) => !c.croDone), [b]);
  const allPendingSelected = pending.length > 0 && pending.every((c) => sel.has(c.containerNo));

  async function pick(kind: 'cro-doc' | 'tax-invoice', file?: File | null) {
    if (!file) return;
    const real = await sniff(file);
    if (kind === 'tax-invoice' && real !== 'pdf') {
      toast.error(`"${file.name}" is not a PDF. The tax invoice must be a PDF — JPG, PNG, Word and Excel files are not accepted.`);
      return;
    }
    if (kind === 'cro-doc' && !real) {
      toast.error(`"${file.name}" is not a PDF or image. Upload the CRO as a PDF, JPG or PNG.`);
      return;
    }
    setUploading(kind);
    try {
      const r = await api.upload<{ fileId: string; name: string }>('/api/procurement/cro/upload', file, { kind, indentId });
      const picked = { file, fileId: r.fileId, name: r.name || file.name, url: URL.createObjectURL(file) };
      if (kind === 'cro-doc') setCroDoc(picked); else setTax(picked);
    } catch (e: any) {
      toast.error(e?.message || 'Upload failed');
    } finally {
      setUploading('');
    }
  }

  const missing = [
    !sel.size && 'select at least one container',
    !croDoc && 'attach the CRO document',
    !validity && 'set the validity',
    !depot.trim() && 'enter depot details',
  ].filter(Boolean) as string[];

  async function submit() {
    if (missing.length) { toast.error(`Before submitting: ${missing.join(', ')}.`); return; }
    setBusy(true);
    try {
      const r = await api.post<{ croRef: string; board: Board; email: { status: string; error?: string } }>(
        `/api/procurement/cro/${encodeURIComponent(indentId)}/submit`,
        {
          containerNos: [...sel], croNumber, validity, depot, sizeType, location, remarks,
          croDoc: { fileId: croDoc!.fileId, name: croDoc!.name },
          taxInvoice: tax ? { fileId: tax.fileId, name: tax.name } : null,
        },
      );
      qc.setQueryData(['procurement', 'cro', indentId], r.board);
      setLastMail(r.email);
      toast.success(`${r.croRef} filed — ${r.board.label}`);
      // Fresh form for the next batch of containers; the board above already shows the new state.
      setSel(new Set()); setCroNumber(''); setRemarks(''); setCroDoc(null); setTax(null);
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      qc.invalidateQueries({ queryKey: ['procurement', 'domestic-queue'] });
    } catch (e: any) {
      toast.error(e?.message || 'CRO submission failed');
      q.refetch();
    } finally {
      setBusy(false);
    }
  }

  const flag = !b ? null : b.status === 'COMPLETE'
    ? { cls: 'border-emerald-300 bg-emerald-50 text-emerald-800', Icon: CheckCircle2 }
    : b.status === 'PARTIAL'
      ? { cls: 'border-amber-300 bg-amber-50 text-amber-800', Icon: Clock }
      : { cls: 'border-slate-300 bg-slate-50 text-slate-700', Icon: Clock };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-3 sm:p-4">
      <div className="flex max-h-[92dvh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 sm:px-5">
          <div>
            <div className="text-sm font-bold text-slate-900">CRO — Container Release Order</div>
            <div className="text-[11px] text-slate-500">{indentId}{b?.productCode ? ` · ${b.productCode}` : ''}{b ? ` · ${b.indentType}` : ''}</div>
          </div>
          <button className="shrink-0 text-2xl leading-none text-slate-400 hover:text-slate-700" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-5">
          {q.isLoading && <div className="py-8 text-center text-sm text-slate-500">Loading containers…</div>}
          {q.isError && <div className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700">{(q.error as Error).message}</div>}

          {b && flag && (
            <>
              {/* The status flag — server-computed, refreshed after every CRO. */}
              <div className={`flex items-center gap-2 rounded-xl border-2 px-4 py-3 ${flag.cls}`}>
                <flag.Icon size={20} />
                <div className="text-base font-extrabold tracking-tight">{b.label}</div>
              </div>
              {!b.applies && (
                <div className="rounded-lg bg-slate-50 px-3 py-2 text-[12px] text-slate-600">
                  {b.indentType} indent — Gate-In is not held back by CRO on this order type.
                </div>
              )}
              {b.legacy && (
                <div className="rounded-lg bg-sky-50 px-3 py-2 text-[12px] text-sky-800">
                  A whole-indent CRO was filed before this form existed (Google Form / old upload), so every container counts as released.
                  {b.legacyDocUrl && <> <a className="font-semibold underline" href={b.legacyDocUrl} target="_blank" rel="noreferrer">Open that CRO</a></>}
                </div>
              )}

              {/* Container checklist */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Containers on this indent</div>
                  {pending.length > 0 && (
                    <button
                      type="button"
                      className="text-[12px] font-semibold text-teal-700 hover:underline"
                      onClick={() => setSel(allPendingSelected ? new Set() : new Set(pending.map((c) => c.containerNo)))}
                    >
                      {allPendingSelected ? 'Clear selection' : `Select all pending (${pending.length})`}
                    </button>
                  )}
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  {b.containers.length === 0 && (
                    <div className="px-3 py-4 text-center text-[12px] text-slate-500">
                      No container numbers are registered on this indent yet. Attach them (Stage 2.1 / Attach containers) before filing a CRO.
                    </div>
                  )}
                  {b.containers.map((c) => {
                    const checked = sel.has(c.containerNo);
                    return (
                      <label
                        key={c.containerNo}
                        className={`flex items-center gap-3 border-b border-slate-100 px-3 py-2 text-sm last:border-b-0 ${c.croDone ? 'bg-emerald-50/50' : checked ? 'bg-teal-50' : 'hover:bg-slate-50 cursor-pointer'}`}
                      >
                        {c.croDone ? (
                          <Lock size={15} className="text-emerald-600" />
                        ) : (
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-teal-600"
                            checked={checked}
                            onChange={() => setSel((s0) => { const n = new Set(s0); if (n.has(c.containerNo)) n.delete(c.containerNo); else n.add(c.containerNo); return n; })}
                          />
                        )}
                        <span className="font-mono font-semibold text-slate-800">{c.containerNo}</span>
                        <span className="ml-auto flex flex-wrap items-center justify-end gap-1.5 text-[11px]">
                          {c.croDone ? (
                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-700" title={c.croAt ? fmt(c.croAt) : ''}>
                              CRO done · {c.croRef}
                            </span>
                          ) : (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">CRO pending</span>
                          )}
                          <span className={`rounded-full px-2 py-0.5 font-semibold ${c.gatedIn ? 'bg-slate-200 text-slate-700' : c.gateInEligible ? 'bg-sky-100 text-sky-700' : 'bg-rose-100 text-rose-700'}`}>
                            {c.gatedIn ? 'Gated in' : c.gateInEligible ? 'Gate-In allowed' : 'Gate-In blocked'}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                  {b.unregistered > 0 && (
                    <div className="bg-slate-50 px-3 py-2 text-[12px] text-slate-500">
                      + {b.unregistered} container{b.unregistered === 1 ? '' : 's'} ordered but not yet registered on the indent — counted as pending.
                    </div>
                  )}
                </div>
              </div>

              {pending.length > 0 && (
                <div className="space-y-3 rounded-xl border border-slate-200 p-3">
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
                    CRO details {sel.size > 0 && <span className="normal-case text-teal-700">— releasing {sel.size} container{sel.size === 1 ? '' : 's'}</span>}
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="text-sm">
                      <span className="mb-1 block font-medium text-slate-600">CRO number</span>
                      <input className="pc-input w-full" value={croNumber} onChange={(e) => setCroNumber(e.target.value)} placeholder="As printed on the release order" />
                    </label>
                    <label className="text-sm">
                      <span className="mb-1 block font-medium text-slate-600">Validity <span className="text-rose-500">*</span></span>
                      <input type="date" className="pc-input w-full" value={validity} onChange={(e) => setValidity(e.target.value)} />
                    </label>
                    <label className="text-sm sm:col-span-2">
                      <span className="mb-1 block font-medium text-slate-600">Depot details <span className="text-rose-500">*</span></span>
                      <input className="pc-input w-full" value={depot} onChange={(e) => setDepot(e.target.value)} placeholder="e.g. Jay Container Terminal, Taloja" />
                    </label>
                    <label className="text-sm">
                      <span className="mb-1 block font-medium text-slate-600">Size / Type</span>
                      <input className="pc-input w-full" value={sizeType} onChange={(e) => setSizeType(e.target.value)} placeholder="e.g. 40' HC Dry" />
                    </label>
                    <label className="text-sm">
                      <span className="mb-1 block font-medium text-slate-600">Location</span>
                      <input className="pc-input w-full" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City / port" />
                    </label>
                    <label className="text-sm sm:col-span-2">
                      <span className="mb-1 block font-medium text-slate-600">Remarks</span>
                      <textarea className="pc-input min-h-[56px] w-full" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                    </label>
                  </div>

                  <FileSlot
                    label="CRO document"
                    required
                    accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
                    hint="PDF, JPG or PNG"
                    value={croDoc}
                    busy={uploading === 'cro-doc'}
                    onPick={(f) => pick('cro-doc', f)}
                    onRemove={() => setCroDoc(null)}
                  />
                  <FileSlot
                    label="Tax invoice"
                    accept="application/pdf,.pdf"
                    hint="Optional · PDF only"
                    value={tax}
                    busy={uploading === 'tax-invoice'}
                    onPick={(f) => pick('tax-invoice', f)}
                    onRemove={() => setTax(null)}
                  />
                </div>
              )}

              {/* Who gets the notification */}
              <div className="flex gap-2 rounded-lg bg-slate-50 px-3 py-2 text-[11.5px] text-slate-600">
                <Mail size={14} className="mt-0.5 shrink-0" />
                <div>
                  Every submission emails <b>{b.recipients.to.join(', ')}</b>
                  {b.recipients.cc.length > 0 && <> · cc {b.recipients.cc.join(', ')}</>}, with the latest container-wise status.
                  {lastMail && (
                    <div className={`mt-1 font-semibold ${lastMail.status === 'sent' ? 'text-emerald-700' : 'text-rose-700'}`}>
                      Last email: {lastMail.status}{lastMail.error ? ` — ${lastMail.error}` : ''}
                    </div>
                  )}
                </div>
              </div>

              {/* Earlier CROs — never edited by later ones */}
              {b.submissions.length > 0 && (
                <div>
                  <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">CRO history</div>
                  <div className="space-y-2">
                    {b.submissions.map((r) => (
                      <div key={r.croRef} className="rounded-lg border border-slate-200 p-2.5 text-[12px]">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <span className="font-mono font-bold text-slate-800">{r.croRef}{r.croNumber ? ` · ${r.croNumber}` : ''}</span>
                          <span className="text-slate-500">{fmt(r.submittedAt)} · {r.submittedByName}</span>
                        </div>
                        <div className="mt-1 font-mono text-slate-700">{r.containerNos.join(', ')}</div>
                        <div className="mt-1 text-slate-500">
                          Valid till {r.validity || '—'} · {r.depot}{r.sizeType ? ` · ${r.sizeType}` : ''}{r.location ? ` · ${r.location}` : ''}
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-2">
                          {r.croDoc && <button className="inline-flex items-center gap-1 text-teal-700 hover:underline" onClick={() => openFileInNewTab(r.croDoc!.fileId)}><FileText size={12} /> CRO document</button>}
                          {r.taxInvoice && <button className="inline-flex items-center gap-1 text-teal-700 hover:underline" onClick={() => openFileInNewTab(r.taxInvoice!.fileId)}><FileText size={12} /> Tax invoice</button>}
                          <span className={`ml-auto ${r.email.status === 'sent' ? 'text-emerald-700' : 'text-rose-600'}`}>
                            Email {r.email.status || 'not sent'}{r.email.error ? ` — ${r.email.error}` : ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
          {b && pending.length > 0 && missing.length > 0 && (
            <span className="mr-auto text-[11.5px] text-slate-500">To submit: {missing.join(' · ')}</span>
          )}
          <button className="btn-ghost" onClick={onClose}>Close</button>
          {b && pending.length > 0 && (
            <button className="btn-primary" disabled={busy || !!uploading || missing.length > 0} onClick={submit}>
              {busy ? 'Submitting…' : `Submit CRO${sel.size ? ` for ${sel.size}` : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FileSlot({ label, required, accept, hint, value, busy, onPick, onRemove }: {
  label: string; required?: boolean; accept: string; hint: string; value: Picked; busy: boolean;
  onPick: (f: File | null) => void; onRemove: () => void;
}) {
  return (
    <div className="text-sm">
      <div className="mb-1 font-medium text-slate-600">
        {label} {required ? <span className="text-rose-500">*</span> : null}
        <span className="ml-1 text-[11px] font-normal text-slate-400">{hint}</span>
      </div>
      {value ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2">
          <FileText size={15} className="text-emerald-700" />
          <span className="min-w-0 flex-1 truncate font-medium text-slate-800" title={value.name}>{value.name}</span>
          <a className="inline-flex items-center gap-1 text-[12px] text-teal-700 hover:underline" href={value.url} target="_blank" rel="noreferrer"><Eye size={13} /> Preview</a>
          <a className="inline-flex items-center gap-1 text-[12px] text-teal-700 hover:underline" href={value.url} download={value.name}><Download size={13} /> Download</a>
          <button type="button" className="inline-flex items-center gap-1 text-[12px] text-rose-600 hover:underline" onClick={onRemove}><X size={13} /> Remove</button>
        </div>
      ) : (
        <label className={`flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2.5 text-[12.5px] text-slate-500 hover:bg-slate-50 ${busy ? 'opacity-60' : ''}`}>
          <Upload size={15} />
          {busy ? 'Uploading…' : `Choose ${label.toLowerCase()}`}
          <input type="file" accept={accept} className="hidden" disabled={busy} onChange={(e) => { onPick(e.target.files?.[0] || null); e.target.value = ''; }} />
        </label>
      )}
    </div>
  );
}
